const routeToCanonicalColorMap: Record<string, string> = {
  carmine: 'red',
  saffran: 'yellow',
  saffron: 'yellow',
  safran: 'yellow',
  ivoire: 'beige',
  ivory: 'beige'
}

const canonicalToRouteColorMap: Record<string, string> = {
  red: 'carmine',
  yellow: 'saffron',
  beige: 'ivoire'
}

const knownCanonicalColors = ['black', 'red', 'yellow', 'beige'] as const

const productAllowedCanonicalColors: Record<string, string[]> = {
  boots: ['black']
}

const PRODUCT_ROUTE_PATTERN = /^\/(?:(fr|es|pt|ja|ko|de|it|zh)\/)?(boots|ankle-boots|pumps|sandals|bottines|bottes|escarpins|sandales)\/([^/?#]+)\/?$/
const PRODUCT_COLLECTION_PATTERN = /^\/(?:(fr|es|pt|ja|ko|de|it|zh)\/)?(boots|ankle-boots|pumps|sandals|bottines|bottes|escarpins|sandales)\/?$/

// FR slugs of product types, used in /fr URLs only (SEO).
const FR_PRODUCT_ROUTE_SLUGS: Record<string, string> = {
  'ankle-boots': 'bottines',
  boots: 'bottes',
  pumps: 'escarpins',
  sandals: 'sandales'
}
const FR_ROUTE_SLUG_TO_PRODUCT_TYPE = Object.fromEntries(
  Object.entries(FR_PRODUCT_ROUTE_SLUGS).map(([type, slug]) => [slug, type])
) as Record<string, string>

export function getProductTypeFromRouteSlug(routeSlug: string) {
  return FR_ROUTE_SLUG_TO_PRODUCT_TYPE[routeSlug] || routeSlug
}

export function getProductTypeRouteSlug(productType: string, localePrefix = '') {
  return localePrefix.replace(/\//g, '') === 'fr' ? FR_PRODUCT_ROUTE_SLUGS[productType] || productType : productType
}

export function normalizeColorRoute(color: string) {
  const normalized = color.trim().toLowerCase()
  return routeToCanonicalColorMap[normalized] || normalized
}

export function isKnownProductColorRoute(color: string) {
  const normalizedColor = normalizeColorRoute(color)
  return (knownCanonicalColors as readonly string[]).includes(normalizedColor)
}

export function toColorRouteSlug(color: string) {
  const normalized = color.trim().toLowerCase()
  return canonicalToRouteColorMap[normalized] || normalized
}

export function toProductRouteColorSlug(productType: string, color: string) {
  const normalizedProductType = productType.trim().toLowerCase()
  const normalizedColor = normalizeColorRoute(color)
  const allowedColors = productAllowedCanonicalColors[normalizedProductType]
  const canonicalColor = allowedColors && !allowedColors.includes(normalizedColor)
    ? allowedColors[0]
    : normalizedColor

  return toColorRouteSlug(canonicalColor)
}

export function getCanonicalProductColorPath(path: string) {
  const normalizedPath = path.replace(/\/+$/, '') || '/'
  const match = normalizedPath.match(PRODUCT_ROUTE_PATTERN)
  if (!match) return null

  const localePrefix = match[1] ? `/${match[1]}` : ''
  const productType = getProductTypeFromRouteSlug(match[2])
  const currentColorSlug = match[3].trim().toLowerCase()
  const canonicalColorSlug = toProductRouteColorSlug(productType, currentColorSlug)

  if (currentColorSlug === canonicalColorSlug) return null

  return `${localePrefix}/${getProductTypeRouteSlug(productType, localePrefix)}/${canonicalColorSlug}`
}

export function isUnknownProductColorPath(path: string) {
  const normalizedPath = path.replace(/\/+$/, '') || '/'
  const match = normalizedPath.match(PRODUCT_ROUTE_PATTERN)
  if (!match) return false

  return !isKnownProductColorRoute(match[3])
}

export function buildProductColorPath(basePath: string, color: string, localePrefix = '') {
  const normalizedBasePath = `/${basePath.replace(/^\/+|\/+$/g, '')}`
  const normalizedLocalePrefix = localePrefix
    ? `/${localePrefix.replace(/^\/+|\/+$/g, '')}`
    : ''
  const productType = normalizedBasePath.replace(/^\//, '')
  const colorSlug = toProductRouteColorSlug(productType, color)
  const routeBasePath = `/${getProductTypeRouteSlug(productType, normalizedLocalePrefix)}`

  return `${normalizedLocalePrefix}${routeBasePath}/${colorSlug}`
}

export function getProductColorPathFromQuery(path: string, rawColor?: string | string[]) {
  const normalizedPath = path.replace(/\/+$/, '') || '/'
  const match = normalizedPath.match(PRODUCT_COLLECTION_PATTERN)
  if (!match) return null

  const colorValue = Array.isArray(rawColor) ? rawColor[0] : rawColor
  if (typeof colorValue !== 'string' || !colorValue.trim()) return null
  if (!isKnownProductColorRoute(colorValue)) return null

  const localePrefix = match[1] ? `/${match[1]}` : ''
  const productType = getProductTypeFromRouteSlug(match[2])

  return buildProductColorPath(`/${productType}`, colorValue, localePrefix)
}
